<template>
    <div
        v-if="product" class="p-relative light-light-background max-size"
        :style="!verticalVersion ? 'padding-inline: 4.25rem;' : ''"
    >
        <image-product v-if="verticalVersion" :image-link="product.imageLink" />

        <ion-grid
            class="purple-purple-color d-flex flex-d-c flex-grow-1 max-width" :class="{'max-height': !verticalVersion}"
        >
            <ion-row class="max-height">
                <ion-col v-if="!verticalVersion" class="center max-height-100">
                    <image-product :image-link="product.imageLink"/>
                </ion-col>

                <ion-col class="center" :class="{ 'mt-3': isApp() }">
                    <div class="max-width" style="max-width: 400px;">
                        <ion-row>
                            <ion-col class="fs-12 grey1-grey1-color">
                                {{ $t('label.availableFrom') }}:
                            </ion-col>
                        </ion-row>

                        <ion-row v-for="supplier of suppliers" :key="supplier.id" class="pb-2">
                            <card-deliverer-reduced :supplier="supplier"/>
                        </ion-row>

                        <ion-row>
                            <ion-col>
                                <span class="octo-gradient-purple-color fw-b fs-larger">
                                    {{ product.name }}
                                </span>
                            </ion-col>
                        </ion-row>

                        <ion-row>
                            <ion-col>
                                {{ product.description }}
                            </ion-col>
                        </ion-row>

                        <template v-if="useUserStore().permission[PERMISSION_KEY.CART_ADD]">
                            <ion-row v-if="isProducts">
                                <ion-col>
                                    <select-base
                                        v-model="variantId"
                                        class="grey1-grey1-color grey4-grey4-background"
                                        style="--original-background: var(--ion-color-grey4)"
                                        :placeholder="$t('placeholder.selectSize')"
                                    >
                                        {{ $t('modal.selection') }}

                                        <template #options>
                                            <template v-for="variant of product.variants" :key="variant.id">
                                                <ion-select-option :value="variant.supplierItem?.id">
                                                    {{ variant.variation }}
                                                </ion-select-option>
                                            </template>
                                        </template>
                                    </select-base>
                                </ion-col>
                            </ion-row>

                            <ion-row class="mt-3">
                                <ion-col size="12">
                                    <input-amount
                                        :amount-step="props.product.amountStep"
                                        v-model="amount"
                                        @change="emit('amount', amount)"
                                    />
                                </ion-col>
                            </ion-row>

                            <ion-row v-if="!isMultiple">
                                <ion-col class="coral-coral-color fs-small center-x">
                                    {{ $t('label.multipleAmount', { target: props.product.amountStep ?? 1 }) }}
                                </ion-col>
                            </ion-row>

                            <ion-row v-if="!isMax">
                                <ion-col class="coral-coral-color fs-small center-x">
                                    {{ $t('label.maxAmount', { target: props.product.amountMax }) }}
                                </ion-col>
                            </ion-row>

                            <ion-row>
                                <ion-col class="center">
                                    <button-base
                                        :color="ION_VARIANT.CORAL"
                                        class="ml-3 mr-3 fw-b wrap-button tt-none max-width"
                                        @click="addCart"
                                        :disabled="!isMultiple || !isMax"
                                    >
                                        {{ $t('modal.addCart') }}
                                    </button-base>
                                </ion-col>
                            </ion-row>
                        </template>
                    </div>
                </ion-col>
            </ion-row>
        </ion-grid>
    </div>
</template>

<script setup lang="ts">
import { IonCol, IonGrid, IonRow, IonSelectOption } from "@ionic/vue";
import { computed, onMounted, ref } from "vue";

import InputAmount from "@/components/input/InputAmount.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";
import ImageProduct from "@/components/various/ImageProduct.vue";
import SelectBase from "@/components/selects/SelectBase.vue";

import { isApp } from "@/functions/enviroment";

import { PromotionOld, PromotionVariant } from "@/types/notification";
import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { PromotionProductEmits } from "@/types/emits";
import { useUserStore } from "@/stores/user";
import { PERMISSION_KEY } from "@/types/user";
import CardDelivererReduced from "@/components/card/CardDelivererReduced.vue";
import { Supplier } from "@/types/supplier";
import { useSupplierStore } from "@/stores/suppliers";

const props = defineProps<{ product: PromotionOld }>()
const emit = defineEmits<PromotionProductEmits>()

const supplierStore = useSupplierStore()

const evaluateAmount = (): number => {
    if (!props.product) return 1

    return props.product.amountStep ?? 1
}

const amount = ref<number>(evaluateAmount())
const variantId = ref<string>('')

const isMax = computed<boolean>(() => {
    if (props.product.amountMax) return amount.value <= props.product.amountMax

    return true
})
const isMultiple = computed<boolean>(() => {
    return (amount.value % (props.product.amountStep ?? 1)) === 0
})
const variant = computed<PromotionVariant | undefined>(() => {
    const variantValue = props.product.variants?.find(variant => variant.supplierItem?.id === variantId.value)

    return variantValue
})
const isProducts = computed<boolean>(() => (props.product.variants?.length ?? 0) > 1)
const suppliers = computed<Supplier[]>(() => {
    if (!props.product || !props.product.supplierIds?.length) return []

    const informationSuppliers: Supplier[] = []
    for (const supplierId of props.product.supplierIds) {
        const participatingSupplier = supplierStore.supplierById(supplierId)
        if (!participatingSupplier) continue
        informationSuppliers.push(participatingSupplier)
    }
    return informationSuppliers
})

const addCart = (): void => {
    if (!variant.value) return;

    emit('addCart', { product: variant.value, amount: amount.value })
}

onMounted(() => {
    variantId.value = props.product.variants ? props.product.variants[0]?.supplierItem?.id ?? '' : ''
})

const verticalVersion = ref<boolean>(isApp() && !window.matchMedia("(orientation: landscape)").matches)
window.addEventListener('orientationchange', () => verticalVersion.value = isApp() && !window.matchMedia("(orientation: landscape)").matches)
window.addEventListener('resize', () => verticalVersion.value = isApp() && !window.matchMedia("(orientation: landscape)").matches)
</script>
